import { MouseEvent, useMemo, useRef, RefObject } from 'react';
import { Box, BoxProps } from '@chakra-ui/react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';
import { capitalize } from 'lodash';
import { Link } from 'react-router-dom';
import CompanyList from 'components/elements/CompanyList';
import { TLeadContext } from 'components/elements/LeadsTable/types';
import Tooltip from 'components/elements/Tooltip';
import { FlexRow } from 'components/layout/FlexRow';
import withProjectVisibility from 'components/widgets/ProjectCard/withProjectVisibility';
import WorkTypeAndVerticalSection from 'components/widgets/ProjectCard/WorkTypeAndVerticalSection';
import SaveProject from 'components/widgets/SaveProject';
import { trackProjectListClick, trackViewProjectDetails } from 'utils/analytics';
import {
  ProjectGroupTypes,
  ViewProjectDetailsClickLocations,
  ViewProjectDetailsClickLocationTypes,
} from 'utils/analytics/constants';
import { formatDate } from 'utils/date';
import { getFormattedAddress, formatZipCode, formatPrice } from 'utils/format';
import { pdpUrl } from 'utils/navigation';
import { renameDocuments } from 'utils/renameDocuments';
import type { PaginatedProject } from 'types';

const basicRowStyle = { width: '100%', marginBottom: 'padding_4' };

const setLastProjectId = (slug: string) => {
  localStorage.setItem('lastProjectId', slug);
};

const headerRowStyle = {
  marginBottom: (theme: any) => theme.space.eight,
  gap: '8px',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const footerRowStyle = {
  ...basicRowStyle,
  justifyContent: 'flex-start',
};

const containerStyle = (isSecondaryItem: boolean) => ({
  alignItems: 'center',
  border: '1px solid',
  borderColor: !isSecondaryItem ? Colors.ArmyGreen200 : Colors.Tangerine700,
  margin: 'auto',
  width: '100%',
  cursor: 'pointer',
  borderRadius: '4px',
  padding: 'padding_8',
  _hover: {
    borderColor: !isSecondaryItem ? Colors.ArmyGreen300 : Colors.Tangerine700,
    boxShadow: '0px 4px 8px 0px #0000001F',
  },
});

const statusContainerStyle = {
  gap: 'padding_8',
  justifyContent: 'flex-start',
};

const statusStyle = (isCompleted: boolean) => ({
  color: isCompleted ? Colors.ArmyGreen900 : Colors.ForestGreen900,
  py: 'padding_4',
  px: '6px',
  borderRadius: '4px',
  background: isCompleted ? Colors.ArmyGreen300 : Colors.ForestGreen200,
});

const workTypeAndVerticalStyle = {
  width: '100%',
};

const workTypeAndVerticalWithCostStyle = {
  width: '80%',
};

const projectCostStyle = {
  textAlign: 'right',
};

const latestActivityStyle = {
  background: Colors.ArmyGreen100,
  padding: 'padding_8',
  borderRadius: '4px',
  mt: 'padding_8',
};

const companyListColumnStyle = { width: '50%' };

const companyListStyle = { justifyContent: 'flex-end' };

const nameStyle = {
  fontSize: '16px',
  fontWeight: 'bold',
  paddingY: 'padding_4',
  display: '-webkit-box',
  WebkitLineClamp: '1',
  WebkitBoxOrient: 'vertical',
  overflowY: 'hidden',
};

const descriptionStyle = {
  display: '-webkit-box',
  WebkitLineClamp: '2',
  WebkitBoxOrient: 'vertical',
  overflowY: 'hidden',
};

const summaryStyle = {
  display: '-webkit-box',
  WebkitLineClamp: '3',
  WebkitBoxOrient: 'vertical',
  overflowY: 'hidden',
};

const activityStatusStyle = {
  marginRight: (theme: any) => theme.space.eight,
};

const saveProjectStyle = { height: 'auto' };

const newTabRowStyle = {
  marginTop: 'padding_4',
  justifyContent: 'flex-end',
};

const goToMapButtonStyle = {
  color: Colors.Black800,
  height: 'auto',
  _hover: {
    textDecor: 'underline',
  },
};

const cityZipStyle = {
  color: Colors.TextQuiet,
  whiteSpace: 'nowrap',
  maxWidth: '40%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

type Props = {
  project: PaginatedProject;
  isSecondaryItem?: boolean;
  leadContext?: TLeadContext;
  clickLocation?: ViewProjectDetailsClickLocationTypes;
  id?: string;
  sx?: BoxProps['sx'];
};

const isClamped = (ref: RefObject<HTMLDivElement>) => {
  // Check if description text has been clamped by the css.
  // Explanation: https://stackoverflow.com/a/67455839
  if (!ref.current) {
    return false;
  }

  return ref.current.scrollHeight > ref.current.clientHeight;
};

const ProjectCard = ({
  project,
  isSecondaryItem = false,
  leadContext = {
    groupId: undefined,
    groupType: ProjectGroupTypes.UNCATEGORIZED,
  },
  clickLocation = ViewProjectDetailsClickLocations.UNCATEGORIZED,
  id,
  sx = {},
}: Props) => {
  const {
    longitude,
    slug,
    name,
    summary,
    address,
    status,
    vertical,
    latest_companies_array,
    latest_company_alias,
    latest_activity: activity,
    work_type_names,
    city,
    zipcode,
    cost,
  } = project;
  const descriptionRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);

  const projectURL = useMemo(() => pdpUrl({ projectSlug: slug }), [slug, pdpUrl]);

  const hasProjectCost = useMemo(() => {
    return Number(cost) > 0;
  }, [cost]);

  const companyList = useMemo(() => {
    // Check if latest_companies_array is available and has items
    if (latest_companies_array && latest_companies_array.length > 0) {
      return latest_companies_array;
    }
    // Fallback to processing latest_company_alias if available
    if (latest_company_alias) {
      return latest_company_alias.split(',').map((company_alias) => ({
        name: company_alias.trim(),
        aliases: [],
        uuid: '',
      }));
    }

    // Return an empty array if neither is available
    return [];
  }, [latest_companies_array, latest_company_alias]);

  const formattedActivityType = renameDocuments(activity?.type);

  const onCardClick = () => {
    setLastProjectId(slug);
    trackProjectListClick({
      projectId: slug,
      groupId: leadContext.groupId,
      groupType: leadContext.groupType,
    });
    trackViewProjectDetails({
      projectId: slug,
      groupId: leadContext.groupId,
      groupType: leadContext.groupType,
      clickLocation,
    });
  };

  const handleOpenInNewTab = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    trackProjectListClick({
      projectId: slug,
      groupId: leadContext.groupId,
      groupType: leadContext.groupType,
    });
    trackViewProjectDetails({
      projectId: slug,
      groupId: leadContext.groupId,
      groupType: leadContext.groupType,
      clickLocation,
    });
    window.open(`${projectURL}`, '_blank');
  };

  const isNameTextClamped = useMemo(() => {
    return isClamped(nameRef);
  }, [nameRef.current]);

  const isSummaryTextClamped = useMemo(() => {
    return isClamped(summaryRef);
  }, [summaryRef.current]);

  const isDescriptionTextClamped = useMemo(() => {
    return isClamped(descriptionRef);
  }, [descriptionRef.current]);

  const cityZipText = useMemo(() => {
    if (!city && !zipcode) {
      return '';
    }
    if (!zipcode) {
      return city;
    }

    return `${city}, ${formatZipCode(zipcode)}`;
  }, [city, zipcode]);

  const style = useMemo(() => {
    return { ...containerStyle(isSecondaryItem), ...sx };
  }, [isSecondaryItem, sx]);

  return (
    <Link to={projectURL} onClick={onCardClick}>
      <Box key={longitude + slug} id={id} sx={style}>
        <FlexRow sx={headerRowStyle}>
          <FlexRow sx={statusContainerStyle}>
            <Text.SmallLabel sx={statusStyle(status === 'Completed')}>
              {status}
            </Text.SmallLabel>
            <div>
              <div>
                <Text.BodyBold>{getFormattedAddress(address)}</Text.BodyBold>
              </div>
              {cityZipText && <Text sx={cityZipStyle}>{cityZipText}</Text>}
            </div>
          </FlexRow>
          <SaveProject
            sx={saveProjectStyle}
            displayBorders={{ savedMenu: false, unsavedMenu: false }}
            showLabel={false}
            projectSlug={slug}
          />
        </FlexRow>
        <FlexRow>
          <Box
            sx={
              hasProjectCost ? workTypeAndVerticalWithCostStyle : workTypeAndVerticalStyle
            }
          >
            <WorkTypeAndVerticalSection
              workTypeList={work_type_names}
              vertical={vertical}
            />
          </Box>
          <Box sx={projectCostStyle}>{hasProjectCost && formatPrice(Number(cost))}</Box>
        </FlexRow>
        {!!name && (
          <Tooltip tooltipText={name} isDisabled={!isNameTextClamped} placement="left">
            <FlexRow sx={{ ...basicRowStyle, ...nameStyle }} ref={nameRef}>
              {name}
            </FlexRow>
          </Tooltip>
        )}
        {!!summary && (
          <Tooltip
            tooltipText={summary}
            isDisabled={!isSummaryTextClamped}
            placement="left"
          >
            <FlexRow ref={summaryRef} sx={{ ...basicRowStyle, ...summaryStyle }}>
              <Text.Body>{summary}</Text.Body>
            </FlexRow>
          </Tooltip>
        )}
        <Box sx={latestActivityStyle}>
          <FlexRow sx={basicRowStyle}>
            <FlexRow.Left>
              <Text.SmallLabel color={Colors.TextQuiet}>LATEST STAGE</Text.SmallLabel>
            </FlexRow.Left>
            <FlexRow.Right>
              {project.activity_count > 1 && (
                <Text color={Colors.TextQuiet}>
                  +{Number(project.activity_count) - 1} past stages
                </Text>
              )}
            </FlexRow.Right>
          </FlexRow>
          <FlexRow sx={basicRowStyle}>
            <FlexRow.Left sx={companyListColumnStyle}>
              <Text.BodyBold>{formattedActivityType}</Text.BodyBold>
            </FlexRow.Left>
            <FlexRow.Right sx={companyListColumnStyle}>
              <CompanyList
                companyList={companyList}
                showLink={false}
                maxCompaniesToShow={1}
                sx={companyListStyle}
              />
            </FlexRow.Right>
          </FlexRow>
          {!summary && (
            <Tooltip
              tooltipText={capitalize(activity?.description)}
              isDisabled={!isDescriptionTextClamped}
              placement="left"
            >
              <FlexRow
                ref={descriptionRef}
                sx={{ ...basicRowStyle, ...descriptionStyle }}
              >
                <Text>{capitalize(activity?.description)}</Text>
              </FlexRow>
            </Tooltip>
          )}
          <FlexRow sx={footerRowStyle}>
            <Text sx={activityStatusStyle}>{activity?.status}</Text>
            <Text color={Colors.TextQuiet}>{formatDate(activity?.date)}</Text>
          </FlexRow>
        </Box>
        <FlexRow sx={newTabRowStyle}>
          <Button
            sx={goToMapButtonStyle}
            variant="unstyled"
            onClick={handleOpenInNewTab}
            endAdornment={<Icon name={Icon.Name.ArrowDiagonal} />}
          >
            Open in new tab
          </Button>
        </FlexRow>
      </Box>
    </Link>
  );
};

export default withProjectVisibility(ProjectCard);
